import React, { FC, useEffect, useRef, useState } from 'react';

import { Button, Grid } from '@material-ui/core';

import { Link } from 'react-router-dom';

import { DateRange } from '@material-ui/pickers';

import { useStyles } from '../styles';

import { useApiError, useLoading } from '../../../../providers';

import { getHighlightData } from '../../IBDashboardService';

import { ChartData, DocumentIcon, LineChart } from '../../../../components';

import { checkDataExist, ClientFilterType, HighlightDataItem, HighlightTypes } from '../../IBDashboardModels';

import { variables } from '../../../../styles';

interface Props {
  dateRange: DateRange<Date>;
  partnership: string;
  currency?: string;
  highlightType: HighlightTypes;
  emptyMessage: string;
  legend: string;
  firstButtonName?: string;
  firstButtonPath?: string;
  secondButtonName?: string;
  secondButtonPath?: string;
  clientFilterType: ClientFilterType;
}

interface HighlightResponse {
  type?: string;
  tType?: string;
  highlights?: HighlightDataItem[] | null;
}

export const GraphPanel: FC<Props> = ({ currency, dateRange, partnership, highlightType, legend, emptyMessage, firstButtonName, firstButtonPath, secondButtonName, secondButtonPath, clientFilterType }) => {
  const classes = useStyles();

  const { addError } = useApiError();
  const { setIsLoading } = useLoading();

  const requestIdRef = useRef(0);
  const addErrorRef = useRef(addError);
  const setIsLoadingRef = useRef(setIsLoading);

  const [chartData, setChartData] = useState<ChartData>();

  const [requestCompleted, setRequestCompleted] = useState(false);

  const fromDate = dateRange[0];
  const toDate = dateRange[1];

  useEffect(() => {
    addErrorRef.current = addError;
  }, [addError]);

  useEffect(() => {
    setIsLoadingRef.current = setIsLoading;
  }, [setIsLoading]);

  useEffect(() => {
    let isMounted = true;

    const requestId = ++requestIdRef.current;

    if (!fromDate || !toDate) {
      setChartData(undefined);
      setRequestCompleted(false);

      return () => {
        isMounted = false;
      };
    }

    const loadData = async () => {
      setIsLoadingRef.current(true);
      setRequestCompleted(false);

      try {
        const response = await getHighlightData(fromDate, toDate, partnership === 'All' ? '' : partnership, highlightType, clientFilterType);

        if (!isMounted || requestId !== requestIdRef.current) {
          return;
        }

        const values: HighlightResponse = response && response.data ? response.data : {};

        const highlights = Array.isArray(values.highlights) ? values.highlights : [];

        setChartData({
          id: values.tType || values.type || String(highlightType),
          data: checkDataExist(highlights, fromDate, toDate),
        });
      } catch (error: any) {
        if (!isMounted || requestId !== requestIdRef.current) {
          return;
        }

        setChartData(undefined);

        addErrorRef.current(JSON.stringify(error && error.response ? error.response : error));
      } finally {
        if (isMounted && requestId === requestIdRef.current) {
          setRequestCompleted(true);
          setIsLoadingRef.current(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [partnership, fromDate, toDate, highlightType, clientFilterType]);

  const hasChartData = Boolean(chartData) && Array.isArray(chartData?.data) && chartData!.data.length > 0;

  return (
    <Grid container direction="column" alignContent="center" className={classes.content}>
      {hasChartData && chartData ? (
        <Grid container direction="row" className={classes.content} justify="space-between">
          <Grid item xs={12} className={classes.chartSection}>
            <LineChart data={[chartData]} currency={currency} colors={[variables.richBlack]} />
          </Grid>

          <Grid item xs={12} className={classes.legend}>
            <span className={classes.dot} />

            <span className={classes.legendTitle}>{legend}</span>
          </Grid>
        </Grid>
      ) : (
        requestCompleted && (
          <Grid container direction="column" alignContent="center" alignItems="center" className={classes.emptyMessage}>
            <Grid item>{emptyMessage}</Grid>
          </Grid>
        )
      )}

      <Grid container className={classes.buttons}>
        <Grid item>
          {firstButtonName && firstButtonPath && (
            <Button startIcon={<DocumentIcon />} component={Link} to={firstButtonPath} className={classes.button}>
              {firstButtonName}
            </Button>
          )}
        </Grid>

        {secondButtonName && secondButtonPath && (
          <Grid item className={classes.secondButton}>
            <Button startIcon={<DocumentIcon />} className={classes.button} component={Link} to={secondButtonPath}>
              {secondButtonName}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
